
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import {
  Megaphone, Plus, Sparkles, ShieldCheck, Wrench, Info,
  AlertTriangle, Eye, Trash2, Send, CheckCircle2, Clock, FileText
} from "lucide-react";

const CATEGORIES = [
  { value: "feature", label: "New Feature", icon: Sparkles, color: "bg-primary/10 text-primary" },
  { value: "gst_update", label: "GST Update", icon: ShieldCheck, color: "bg-emerald-500/10 text-emerald-600" },
  { value: "maintenance", label: "Maintenance", icon: Wrench, color: "bg-amber-500/10 text-amber-600" },
  { value: "general", label: "General", icon: Info, color: "bg-secondary text-secondary-foreground" },
  { value: "urgent", label: "Urgent", icon: AlertTriangle, color: "bg-destructive/10 text-destructive" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const ROLE_OPTIONS = [
  { value: "company_admin", label: "Company Admins" },
  { value: "ca", label: "CAs" },
  { value: "accountant", label: "Accountants" },
];

export default function AnnouncementsPage() {
  const { user, role } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = role === "super_admin" || role === "company_admin";
  const [showCreate, setShowCreate] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: "", content: "", category: "general", priority: "normal", targetRoles: [] as string[],
  });

  const { data: announcements = [] } = useQuery({
    queryKey: ["announcements"],
    queryFn: async () => {
      const { data, error } = await supabase.from("announcements").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: reads = [] } = useQuery({
    queryKey: ["announcement-reads"],
    queryFn: async () => {
      const { data, error } = await supabase.from("announcement_reads").select("announcement_id");
      if (error) throw error;
      return data.map((r: any) => r.announcement_id);
    },
  });

  const createMutation = useMutation({
    mutationFn: async (publish: boolean) => {
      if (!user) throw new Error("Not logged in");
      const { error } = await supabase.from("announcements").insert({
        author_user_id: user.id,
        author_name: user.name,
        title: form.title.trim(),
        content: form.content.trim(),
        category: form.category,
        priority: form.priority,
        target_roles: form.targetRoles,
        is_published: publish,
        published_at: publish ? new Date().toISOString() : null,
      });
      if (error) throw error;
    },
    onSuccess: (_, publish) => {
      toast({ title: publish ? "Announcement published!" : "Saved as draft" });
      setShowCreate(false);
      setForm({ title: "", content: "", category: "general", priority: "normal", targetRoles: [] });
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
    onError: () => toast({ title: "Error creating announcement", variant: "destructive" }),
  });

  const markReadMutation = useMutation({
    mutationFn: async (announcementId: string) => {
      if (!user) return;
      await supabase.from("announcement_reads").insert({ announcement_id: announcementId, user_id: user.id });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["announcement-reads"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("announcements").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Announcement deleted" });
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("announcements").update({ is_published: true, published_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Announcement published!" });
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
  });

  const published = announcements.filter((a: any) => a.is_published);
  const drafts = announcements.filter((a: any) => !a.is_published);
  const unreadCount = published.filter((a: any) => !reads.includes(a.id)).length;
  const previewItem = announcements.find((a: any) => a.id === previewId);

  const getCategoryConfig = (cat: string) => CATEGORIES.find((c) => c.value === cat) || CATEGORIES[3];

  const toggleRole = (r: string) => {
    setForm((f) => ({
      ...f,
      targetRoles: f.targetRoles.includes(r) ? f.targetRoles.filter((x) => x !== r) : [...f.targetRoles, r],
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Announcements</h1>
          <p className="text-muted-foreground text-sm">
            {isAdmin ? "Broadcast updates to your users" : "Stay updated with the latest news"}
            {unreadCount > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{unreadCount} new</Badge>}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button><Plus className="h-4 w-4 mr-2" />New Announcement</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create Announcement</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Category</label>
                  <div className="grid grid-cols-3 gap-2">
                    {CATEGORIES.map((c) => (
                      <button
                        key={c.value}
                        onClick={() => setForm({ ...form, category: c.value })}
                        className={`flex items-center gap-2 p-2.5 rounded-lg border text-xs font-medium transition-colors ${
                          form.category === c.value ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/50"
                        }`}
                      >
                        <c.icon className="h-4 w-4" />{c.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Input placeholder="Announcement title..." value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                <Textarea placeholder="Write your announcement..." rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">Priority</label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">Target Audience</label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {ROLE_OPTIONS.map((r) => (
                        <button
                          key={r.value}
                          onClick={() => toggleRole(r.value)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            form.targetRoles.includes(r.value) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
                          }`}
                        >{r.label}</button>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Leave empty = all users</p>
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => createMutation.mutate(false)} disabled={!form.title.trim()}>
                    <FileText className="h-4 w-4 mr-2" />Save Draft
                  </Button>
                  <Button onClick={() => createMutation.mutate(true)} disabled={!form.title.trim() || !form.content.trim()}>
                    <Send className="h-4 w-4 mr-2" />Publish Now
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Published", value: published.length, icon: Megaphone },
          { label: "Unread", value: unreadCount, icon: Eye },
          { label: "Drafts", value: drafts.length, icon: FileText },
          { label: "Critical", value: published.filter((a: any) => a.priority === "critical").length, icon: AlertTriangle },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className="h-8 w-8 text-primary" />
              <div><p className="text-2xl font-bold text-foreground">{s.value}</p><p className="text-xs text-muted-foreground">{s.label}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="published">
        <TabsList>
          <TabsTrigger value="published">Published{unreadCount > 0 ? ` (${unreadCount} new)` : ""}</TabsTrigger>
          {isAdmin && <TabsTrigger value="drafts">Drafts ({drafts.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="published" className="space-y-3">
          {published.length === 0 && (
            <div className="text-center py-16"><Megaphone className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" /><p className="text-muted-foreground">No announcements yet.</p></div>
          )}
          {published.map((a: any) => {
            const cat = getCategoryConfig(a.category);
            const CatIcon = cat.icon;
            const isRead = reads.includes(a.id);
            return (
              <Card key={a.id} className={`transition-colors ${!isRead ? "border-primary/30 bg-primary/5" : "hover:bg-muted/20"}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${cat.color} flex-shrink-0`}>
                      <CatIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-foreground text-sm">{a.title}</h3>
                        {!isRead && <Badge className="bg-primary text-primary-foreground text-[10px] h-5">New</Badge>}
                        {a.priority === "critical" && <Badge variant="destructive" className="text-[10px] h-5">Critical</Badge>}
                        {a.priority === "high" && <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/30 text-[10px] h-5">High</Badge>}
                        <Badge variant="outline" className="text-[10px] h-5 capitalize">{cat.label}</Badge>
                      </div>
                      <p className="text-sm text-foreground/80 line-clamp-2 mb-2">{a.content}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{a.author_name}</span>
                        <span>•</span>
                        <span>{new Date(a.published_at || a.created_at).toLocaleDateString()}</span>
                        {a.target_roles?.length > 0 && (
                          <>{" • "}{a.target_roles.map((r: string) => ROLE_OPTIONS.find((o) => o.value === r)?.label || r).join(", ")}</>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {!isRead && (
                        <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => markReadMutation.mutate(a.id)}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />Read
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPreviewId(a.id)}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {isAdmin && a.author_user_id === user?.id && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteMutation.mutate(a.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="drafts" className="space-y-3">
            {drafts.length === 0 && <p className="text-center py-12 text-muted-foreground">No drafts.</p>}
            {drafts.map((a: any) => {
              const cat = getCategoryConfig(a.category);
              const CatIcon = cat.icon;
              return (
                <Card key={a.id} className="hover:bg-muted/20 transition-colors">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${cat.color}`}><CatIcon className="h-4 w-4" /></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground text-sm truncate">{a.title}</h3>
                      <p className="text-xs text-muted-foreground">Created {new Date(a.created_at).toLocaleDateString()}</p>
                    </div>
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => publishMutation.mutate(a.id)}>
                      <Send className="h-3 w-3 mr-1" />Publish
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteMutation.mutate(a.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        )}
      </Tabs>

      {/* Preview dialog */}
      <Dialog open={!!previewId} onOpenChange={() => setPreviewId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{previewItem?.title}</DialogTitle></DialogHeader>
          {previewItem && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="capitalize">{getCategoryConfig(previewItem.category).label}</Badge>
                <Badge variant="outline" className="capitalize">{previewItem.priority} priority</Badge>
                {previewItem.target_roles?.length > 0 && <Badge variant="secondary">Targeted</Badge>}
              </div>
              <div className="border rounded-lg p-4 bg-muted/20">
                <pre className="whitespace-pre-wrap text-sm text-foreground font-sans">{previewItem.content}</pre>
              </div>
              <p className="text-xs text-muted-foreground">By {previewItem.author_name} • {new Date(previewItem.published_at || previewItem.created_at).toLocaleString()}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
